import { OrganisationProductSummary } from "@fero/components";
import type { StorageLocationProductSale } from "@fero/pages";
import { Stack, TextField, Typography } from "@mui/material";
import type { ProductDate } from "adl-gen/ferovinum/app/db";
import {
  type ColumnDef,
  GridTable,
  GridTableColumnFactory,
} from "components/widgets/grid-table/grid-table";
import type React from "react";
import { useMemo } from "react";
import { getVesselCapacity, type VesselCapacity } from "utils/model-utils";
import { useManageHsCodes } from "../hooks/use-manage-hs-codes";

// Improved type definitions
interface ProductValue {
  hsCode: string;
  productDate: ProductDate;
  name: string;
  code: string;
  producerName: string;
  vesselCapacity: VesselCapacity | undefined;
}

interface TransformedProduct {
  id?: string | number;
  productId: string;
  product: {
    id: string;
    value: ProductValue;
  };
}

interface CustomCodesStepProps {
  products?: StorageLocationProductSale[];
}

export const CustomCodesStep: React.FC<CustomCodesStepProps> = ({
  products = [],
}) => {
  const {
    localHSCode,
    hsCodeErrors,
    handleHSCodeChange,
    handleHSCodeBlur,
    localHSCodeRef,
    initialHSCodeSet,
  } = useManageHsCodes(products);

  const sortedTransformedProducts = useMemo(() => {
    return products
      .map((product) => {
        const hsCode = initialHSCodeSet
          ? localHSCodeRef.current[product.product.id] || ""
          : "";
        return {
          productId: product.product.id,
          product: {
            id: product.product.id,
            value: {
              hsCode, // HS Code comes from fetched or local state
              productDate: product.product.value.productDate,
              name: product.product.value.name,
              code: product.product.value.code,
              producerName: product.product.value.producerName,
              vesselCapacity: getVesselCapacity(
                product.product.value.vesselSize,
              ),
            },
          },
        } as TransformedProduct;
      })
      .sort((a, b) => {
        // Implement your sorting logic here based on hsCode or other properties
        if (a.product.value.hsCode < b.product.value.hsCode) return -1;
        if (a.product.value.hsCode > b.product.value.hsCode) return 1;
        return 0; // If HS codes are equal, no sorting
      });
  }, [products, initialHSCodeSet, localHSCodeRef]);

  const factory = new GridTableColumnFactory<TransformedProduct>();
  const COLUMNS: ColumnDef<TransformedProduct, string>[] = [
    factory.makeNodeColumn({
      header: "Product",
      key: "product",
      width: 200,
      renderNode: (row) => (
        <OrganisationProductSummary
          code={row.product.value.code}
          name={row.product.value.name}
          producerName={row.product.value.producerName}
          productDate={row.product.value.productDate}
          vesselCapacity={row.product.value.vesselCapacity}
        />
      ),
    }),
    factory.makeNodeColumn({
      header: "HS Code",
      key: "hs code",
      flex: 0.5,
      width: 100,
      renderNode: (row) => (
        <TextField
          label="HS Code"
          value={localHSCode[row.product.id] || ""}
          onChange={(e) => handleHSCodeChange(row.product.id, e.target.value)}
          onBlur={() => handleHSCodeBlur(row.product.id)}
          fullWidth
          error={!!hsCodeErrors[row.product.id]}
          helperText={hsCodeErrors[row.product.id] || ""}
        />
      ),
    }),
  ];

  return (
    <Stack
      spacing={2}
      sx={{
        backgroundColor: "white",
        borderRadius: 1,
      }}
      padding={4}
      width="100%"
    >
      <Typography variant="h6">Custom Codes</Typography>
      <Typography variant="body1" sx={{ pb: 2 }}>
        Please provide or verify HS codes for each product listed below. These
        codes will be included on the invoice for this international sale.
      </Typography>
      <GridTable inputRowsField={sortedTransformedProducts} columns={COLUMNS} />
    </Stack>
  );
};
